import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicContractCancellationResolveResponseDto } from "@madamuh/domain";
import { ApiError } from "../../shared/api/client";
import {
  confirmPublicContractCancellation,
  resolvePublicContractCancellation
} from "../../shared/api/commercialContract";

function consumeTokenFromFragment(): string {
  const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token")?.trim() ?? "";
  if (window.location.hash) {
    window.history.replaceState(null, document.title, window.location.pathname);
  }
  return token;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function normalStatusMessage(status: PublicContractCancellationResolveResponseDto["status"]): string | null {
  switch (status) {
    case "previewChanged":
      return "解約条件が更新されました。最新内容を確認し、もう一度「解約を確定する」を押してください。";
    case "expired":
      return "このリンクは期限切れです。管理画面から手続きをやり直してください。";
    case "invalid":
      return "このリンクは利用できません。最新のメールをご確認ください。";
    case "alreadyUsed":
      return "このリンクはすでに使用済みです。";
    case "revoked":
      return "このリンクは無効化されています。";
    case "valid":
      return null;
  }
}

function temporaryErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 503 || error.code === "CONTRACT_CANCELLATION_LOOKUP_UNAVAILABLE") {
      return "解約確認サービスを一時的に利用できません。時間をおいてもう一度お試しください。";
    }
    if (error.status === 500) {
      return "解約確認処理でシステムエラーが発生しました。時間をおいてもう一度お試しください。";
    }
    if (error.status === 404) {
      return "解約確認サービスが見つかりません。システムの反映状況をご確認ください。";
    }
  }
  return "解約確認サービスに接続できませんでした。通信環境をご確認ください。";
}

function isTemporaryError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 500 || error.status === 503 || error.code === "CONTRACT_CANCELLATION_LOOKUP_UNAVAILABLE";
}

export function ContractCancellationActionPage() {
  const tokenRef = useRef("");
  const resolveInFlightRef = useRef(false);
  const [resolution, setResolution] = useState<PublicContractCancellationResolveResponseDto | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [canRetry, setCanRetry] = useState(false);

  const resolveToken = useCallback(async (rawToken: string) => {
    if (!rawToken) {
      setMessage("確認に必要な情報がありません。");
      setCanRetry(false);
      return;
    }
    if (resolveInFlightRef.current) return;

    resolveInFlightRef.current = true;
    setLoading(true);
    setCanRetry(false);
    try {
      const result = await resolvePublicContractCancellation(rawToken);
      setResolution(result);
      setMessage(normalStatusMessage(result.status));
    } catch (error) {
      setResolution(null);
      setMessage(temporaryErrorMessage(error));
      setCanRetry(isTemporaryError(error));
    } finally {
      resolveInFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  const consumeAndResolveFragment = useCallback(() => {
    const token = consumeTokenFromFragment();
    if (!token) return;
    tokenRef.current = token;
    void resolveToken(token);
  }, [resolveToken]);

  const retryCurrentToken = useCallback(() => {
    if (!tokenRef.current) {
      setMessage("確認に必要な情報がありません。");
      setCanRetry(false);
      return;
    }
    void resolveToken(tokenRef.current);
  }, [resolveToken]);

  useEffect(() => {
    consumeAndResolveFragment();
  }, [consumeAndResolveFragment]);

  useEffect(() => {
    window.addEventListener("hashchange", consumeAndResolveFragment);
    window.addEventListener("pageshow", consumeAndResolveFragment);
    return () => {
      window.removeEventListener("hashchange", consumeAndResolveFragment);
      window.removeEventListener("pageshow", consumeAndResolveFragment);
    };
  }, [consumeAndResolveFragment]);

  async function confirm() {
    if (!tokenRef.current || !resolution?.confirmationFingerprint) return;
    setConfirming(true);
    try {
      const result = await confirmPublicContractCancellation(
        tokenRef.current,
        resolution.confirmationFingerprint
      );
      setMessage(`解約予約が完了しました。利用終了日: ${formatDateTime(result.serviceEndsAt)}`);
      setResolution((current) => (current ? { ...current, status: "alreadyUsed" } : current));
    } catch {
      setMessage("解約条件が更新されたか、リンクが利用できません。最新内容を読み込んでから再度お試しください。");
    } finally {
      setConfirming(false);
    }
  }

  const preview = resolution?.preview;
  const canConfirm =
    (resolution?.status === "valid" || resolution?.status === "previewChanged") &&
    Boolean(resolution?.confirmationFingerprint);

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <section style={{ display: "grid", gap: 16, padding: 20, border: "1px solid #e5e7eb", borderRadius: 16 }}>
        <h1 style={{ margin: 0 }}>解約予約の確認</h1>
        {loading ? <p>確認リンクを読み込んでいます。</p> : null}
        {message ? <p>{message}</p> : null}
        {preview ? (
          <>
            <dl>
              <div><dt>クラブ</dt><dd>{preview.tenantDisplayName}</dd></div>
              <div><dt>契約連絡先メール</dt><dd>{resolution?.contractContactMaskedEmail}</dd></div>
              <div><dt>利用終了日</dt><dd>{formatDateTime(preview.serviceEndsAt)}</dd></div>
              <div><dt>最初に利用しない月</dt><dd>{preview.firstSkippedServiceMonth}</dd></div>
              <div><dt>取消可能期限</dt><dd>{formatDateTime(preview.cancellationRevocableUntil)}</dd></div>
            </dl>
            <button type="button" disabled={!canConfirm || confirming} onClick={() => void confirm()}>
              {confirming ? "確定中..." : "解約を確定する"}
            </button>
            <p>この画面を開くだけでは解約予約は成立しません。</p>
          </>
        ) : null}
        {canRetry ? (
          <button type="button" disabled={loading} onClick={retryCurrentToken}>
            もう一度読み込む
          </button>
        ) : null}
      </section>
    </main>
  );
}
